import { createApp } from 'vue'
import * as Sentry from '@sentry/vue'

import { useDjangoAuthStore } from '@/stores/authDjango'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'
import 'vuetify/dist/vuetify.min.css'

import { useApi } from '@/api/useApi'
import HeaderImage from '/header.jpeg'
import HeaderImageLazy from '/header_lazy.jpg'
import { pinia } from '@/modules/pinia'
import apiUrls from '@/api/apiUrls'

import { ComponentLibrary, useEventsBus } from '@haskoning/vue-component-library'
import { AuthenticationManagement } from '@haskoning/vue-authentication-middleware'

pinia.use(({ store }) => {
  store.$api = api
  store.$apiUrls = apiUrls
})

const app = createApp(App).use(vuetify).use(pinia).use(router)
const { api } = useApi()

const authStore = useDjangoAuthStore(pinia)
const { bus } = useEventsBus()

app.use(ComponentLibrary, { externalBus: bus })
app
  .use(AuthenticationManagement, {
    appTitle: 'ARMOUR',
    headerImage: HeaderImage,
    appSubTitle: false,
    headerImageLazy: HeaderImageLazy,
    feathers: api,
    authStore: authStore,
    apiBaseUrl: import.meta.env.VITE_APP_API_BASE_URL,
    showForgotPassword: false,
    showUsernameAndPasswordLogin: false
  })
  .provide('useAuthStore', authStore)

Sentry.init({
  app,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracePropagationTargets: ['*'],
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      triggerLabel: 'Feedback',
      formTitle: 'Send Feedback',
      submitButtonLabel: 'Send Feedback',
      cancelButtonLabel: 'Cancel',
      messagePlaceholder: 'What would you like to tell us?',
      successMessageText: 'Thank you for your feedback!',
      colorScheme: 'light',
      showName: true,
      showEmail: true,
      enableScreenshot: true,
      autoInject: false
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1, // Capture 10% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in cloud and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

router.isReady().then(() => {
  app.mount('#app')

  const feedback = Sentry.getFeedback()
  if (!feedback) return

  let widget: ReturnType<typeof feedback.createWidget> | null = null

  const isAuthenticated = () => {
    return authStore.user !== null
  }

  const updateFeedbackWidget = () => {
    if (isAuthenticated() && !widget) {
      widget = feedback.createWidget()
      widget.appendToDom()
      if (authStore.user?.email) {
        Sentry.setUser({
          username: authStore.user.first_name ?? '',
          email: authStore.user.email ?? ''
        })
      }
    } else if (!isAuthenticated() && widget) {
      widget.removeFromDom()
      widget = null
      Sentry.setUser(null)
    }
  }
  updateFeedbackWidget()
  authStore.$subscribe(updateFeedbackWidget)
})
